import { Button, FormLabel, Grid, Input, Stack, styled, Textarea, Typography } from '@mui/joy';
import { css } from '@mui/system';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { OrganizationAdminLevelsTable } from '@/components/organizations/table/OrganizationAdminLevelsTable';
import { I18N_DE } from '@/i18n/de';
import { Organization } from '@/modules/api';
import { backendApiOrganizationsUpdateMutation } from '@/modules/api/@tanstack/react-query.gen';
import { useMyOrganizationQuery } from '@/queries/useMyOrganizationQuery';
import { EntityActive } from '@/types/EntityActive';

const StyledFormLabel = styled(FormLabel)(
  ({ theme }) => css`
    font-weight: 400;
    font-size: 14px;
    color: ${theme.palette.text.tertiary};
  `,
);

const generateLetterHead = (organization: Organization) => `${organization.name}
${organization.street || ''}
${organization.postalCode || ''} ${organization.city || ''}
${organization.email || ''}
${organization.phoneNumber || ''}`;

type FormData = Pick<Organization, 'letterHead'>;

function RouteComponent() {
  const { data: organization, isFetching } = useMyOrganizationQuery({
    adminLevels: true,
    users: true,
    stakeholders: true,
  });

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { isDirty },
  } = useForm<FormData>();
  const { mutate, isPending } = useMutation(backendApiOrganizationsUpdateMutation());

  useEffect(() => organization && reset({ letterHead: organization.letterHead }), [reset, organization]);

  return (
    organization && (
      <Grid container spacing={2}>
        <Grid md={6} xs={12}>
          <Stack gap={1}>
            <Typography level="h3">Organisation</Typography>
            <Stack>
              <StyledFormLabel>Typ</StyledFormLabel>
              <Input size="md" value={I18N_DE.OrganizationType[organization.type]} readOnly />
            </Stack>
            <Stack>
              <StyledFormLabel>Status</StyledFormLabel>
              <Input
                size="md"
                value={I18N_DE.EntityActive[organization.active ? EntityActive.TRUE : EntityActive.FALSE]}
                readOnly
              />
            </Stack>

            <Typography level="h3" marginTop={2}>
              Kontaktinformationen
            </Typography>
            <Stack direction="row" gap={1}>
              <Stack width="100%">
                <StyledFormLabel>Postleitzahl</StyledFormLabel>
                <Input size="md" value={organization.postalCode || ''} readOnly />
              </Stack>
              <Stack width="100%">
                <StyledFormLabel>Ort</StyledFormLabel>
                <Input size="md" value={organization.city || ''} readOnly />
              </Stack>
            </Stack>
            <Stack>
              <StyledFormLabel>Straße</StyledFormLabel>
              <Input size="md" value={organization.street || ''} readOnly />
            </Stack>
            <Stack direction="row" gap={1}>
              <Stack width="100%">
                <StyledFormLabel>Telefonnummer</StyledFormLabel>
                <Input size="md" value={organization.phoneNumber || ''} readOnly />
              </Stack>
              <Stack width="100%">
                <StyledFormLabel>Mobiltelefonnummer</StyledFormLabel>
                <Input size="md" value={organization.mobileNumber || ''} readOnly />
              </Stack>
            </Stack>
            <Stack>
              <StyledFormLabel>E-Mail</StyledFormLabel>
              <Input size="md" value={organization.email || ''} readOnly />
            </Stack>

            <Typography level="h3" marginTop={2}>
              Briefkopf
            </Typography>
            <Stack
              component="form"
              onSubmit={handleSubmit((data) => {
                if (isPending) return;

                mutate({ path: { organizationId: organization.id }, body: data });
              })}
            >
              <StyledFormLabel>Briefkopf</StyledFormLabel>
              <Textarea
                variant="outlined"
                sx={{ p: 1 }}
                minRows={5}
                size="md"
                slotProps={{ textarea: { ...register('letterHead') } }}
              />
              <Stack direction="row" gap={1} marginTop={2}>
                <Button
                  variant="outlined"
                  size="md"
                  onClick={() =>
                    setValue('letterHead', generateLetterHead(organization), {
                      shouldDirty: true,
                    })
                  }
                >
                  aus Kontaktinformationen übernehmen
                </Button>
                <Button size="md" type="submit" disabled={!isDirty || isPending} loading={isPending}>
                  speichern
                </Button>
              </Stack>
            </Stack>
          </Stack>
        </Grid>
        <Grid md={6} xs={12}>
          <Stack gap={1}>
            <Typography level="h3" marginBottom={2.5}>
              Zugeordnete Verwaltungsebenen
            </Typography>
            <OrganizationAdminLevelsTable
              data={organization.adminLevels}
              isFetching={isFetching}
              show="adminLevels"
              noActionOnClick
            />
          </Stack>
        </Grid>
      </Grid>
    )
  );
}

export const Route = createFileRoute('/_authenticated/_management/meine-organisation/stammdaten/')({
  component: RouteComponent,
});
